<template>
  <div
    v-if="visible"
    class="merge-members-topbar-banner d-flex align-items-center"
    data-testid="suggested-merges-banner"
  >
    <a
      :href="mergeMembersUrl"
      class="banner-link suggested-merges-banner-link d-flex align-items-center text-decoration-none flex-grow-1"
      data-testid="suggested-merges-banner-link"
    >
      <AppTooltipIcon
        :message="tooltipMessage"
        placement="auto bottom"
      />
      <span
        class="text-dark"
        data-testid="suggested-merges-banner-text"
      >
        {{ bannerText }}
      </span>
    </a>

    <i
      class="hi hi-close hi-10 clickable mr-10"
      @click="dismiss"
    />
  </div>
</template>

<script>
import { pluralize } from '~/utils/string_utils';
import MembersTableUpdateChannel from '~/channels/organization_members_table_update_channel';
import { OrganizationsResource } from '~/resources';

const STORAGE_KEY = 'merge_members_banner_dismissed_ids';

function loadDismissedIds() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);

    if (!stored) {
      return [];
    }

    return JSON.parse(stored) || [];
  } catch {
    return [];
  }
}

function saveDismissedIds(ids) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
}

export default {
  name: 'SuggestedMergesBanner',
  props: {
    organizationId: { type: Number, required: true },
    initialCount: { type: Number, default: 0 },
    initialIds: { type: Array, default: () => [] },
  },
  data() {
    const dismissed = loadDismissedIds();
    const shouldShow = this.initialIds.some((id) => !dismissed.includes(id));

    return {
      count: this.initialCount,
      minDaysLeft: null,
      loading: false,
      visible: shouldShow,
      subscription: null,
      mergeIds: this.initialIds,
    };
  },
  computed: {
    allBilled() {
      return this.minDaysLeft == null;
    },
    daysLeft() {
      return this.minDaysLeft || 0;
    },
    bannerText() {
      const countdown =
        !this.allBilled && this.daysLeft > 0 ? ` (${this.daysLeft} ${pluralize(this.daysLeft, 'day')} left)` : '';

      return `${this.count} suggested ${pluralize(this.count, 'merge')}${countdown}`;
    },
    mergeMembersUrl() {
      return `/organizations/${this.organizationId}/merge_members`;
    },
    tooltipMessage() {
      return 'Possible duplicates based on name or username similarities were found. Please take a moment to review them.';
    },
  },
  mounted() {
    this.fetchData();
    this.subscription = MembersTableUpdateChannel.subscribe(this.organizationId, () => {
      this.fetchData();
    });
  },
  beforeUnmount() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  },
  methods: {
    dismiss() {
      const dismissed = new Set(loadDismissedIds());

      this.mergeIds.forEach((id) => dismissed.add(id));
      saveDismissedIds(Array.from(dismissed));
      this.visible = false;
    },
    async fetchData() {
      this.loading = true;
      try {
        const res = await OrganizationsResource.mergeMembersRecommendations({ id: this.organizationId });
        const merges = res.suggested_merges || [];
        const pagination = res.pagination || null;

        this.count = pagination ? pagination.total_count : merges.length;
        this.mergeIds = Array.from(new Set([...(this.mergeIds || []), ...merges.map((m) => m.recommendation_id)]));

        let min = null;

        merges.forEach((m) => {
          const dl = m.duplicate.days_left;

          if (dl != null && dl > 0) {
            if (min == null || dl < min) {
              min = dl;
            }
          }
        });
        this.minDaysLeft = min;

        const dismissedIds = loadDismissedIds();
        const unseen = this.mergeIds.some((id) => !dismissedIds.includes(id));

        this.visible = unseen && this.count > 0;
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
